import { IEventType } from 'model/eventType';
import { IClient } from 'model/client';

import { Moment } from 'moment';
import { calendarGroup } from './groups';
import {
  IEvent,
  IFullAppointmentType,
  IRecurrencePattern,
  IClinic
} from 'model/v2';

export interface ICardEvent {
  id: string;
  subject: string;
  startTime: Moment;
  endTime: Moment;
  status: Status;
  locationType: string;
  masterID?: string;
  locationCategory?: string;
  appointmentType: IAppointment;
  clinic: IClinic;
  therapist: ITherapist;
  room: IRoom;
  type: EventType;
  client: IClient;
  isPendingConfirmation: boolean;
  recurringEvery: number;
  crStatus: number;
  athenaStatus: number;
}

export interface ICalendarEventsFetcher {
  cardEvents: ICardEvent;
  appointmentTypes: IAppointmentType[];
  appointmentABATypes: IAppointmentType[];
  adminAppointmentTypes: IAdminAppointment[];
}

export interface IAllAppointmentTypes {
  appointmentTypes: IAppointmentType[];
  appointmentABATypes: IAppointmentType[];
  adminAppointmentTypes: IAdminAppointment[];
}

export interface IAppointment {
  id: number;
  isClinical: boolean;
  subType: string;
  title: string;
}
export interface IAppointmentTypeEvent extends IAppointment {
  subType: string;
}
export interface IAppointmentType extends IAppointment {
  id: number;
  title: string;
  headerColor: string;
  backgroundColor: string;
  eventType: IEventType;
  subTypes: ISubType;
}
export interface IAdminAppointment {
  id: number;
  value: string;
}

export interface ISubType {
  id: string;
  title: string;
  athenaType: string;
  crType: string;
  superType: string;
  duration: number;
}

export interface ITherapist {
  id: string;
  name: string;
}
export interface IRoom {
  roomName: string;
}

export interface ICalendarEvent extends IEvent {
  clinics?: any;
  start?: Date;
  end?: Date;
  /**
   * The event's instants as stored, kept when `start` / `end` have been moved
   * onto a clock other than the viewer's for drawing (AXON-3603). Use these —
   * never `start` — to compare against a server value or to feed a formatter
   * that applies its own timezone conversion.
   */
  trueStart?: Date;
  trueEnd?: Date;
  startTime?: Moment;
  endTime?: Moment;
  resourceId?: string;
  title?: string;
  desc?: IEventDesc;
  groupType: calendarGroup;
  calendarID?: string;
  canMove?: boolean;
  baseId?: string;
  color?: string;
  headerColor?: string;
  bgColor?: string;
  chipColor?: string;
  availType?: string;
  isAdmin?: boolean;
  plannerWeekStartTime?: Moment;
  plannerWeekEndTime?: Moment;
  observationsCount?: number;
  evalSlotLocation?: any;
  isDelete?: boolean;
  /**
   * Client-only, transient flag set optimistically when the user cancels this
   * event, so the planner shows the "In-progress Cancel in Axon" hover status
   * immediately (before the async cancel removes the event). It is read directly
   * by ItemTooltip because the tile overrides crStatus/athenaStatus with its own
   * useEventSyncStatus values, which would otherwise discard an optimistic status.
   */
  isCancelInProgress?: boolean;
  /** True once the event has been converted (drives the striped tile background). */
  isConverted?: boolean;
  makeupSessionEventId?: number;
  masterId?: number;
  /** 0 on a series' first occurrence; later occurrences count up from there. */
  instanceIndex?: number;
  /** Set when a series is projected onto the template from a future week. */
  seriesStartDate?: Date | string;
}

export interface IPhantomEvent {
  resourceId?: string;
  baseId?: string;
  title?: string;
  observationsCount?: number;
  appointmentType?: IFullAppointmentType;
  groupType?: calendarGroup;
  calendarID?: string;
  canMove?: boolean;
  startTime?: Moment;
  endTime?: Moment;
  type?: EventType;
  recurrencePattern?: IRecurrencePattern;
  status?: string;
  startDate?: Moment;
  plannerWeekStartTime?: Moment;
  plannerWeekEndTime?: Moment;
  locationType?: string;
  start?: Date;
  end?: Date;
  clinic?: IClinic;
  isPendingConfirmation?: boolean;
}

export interface IEventDesc {
  main: string;
  extras: string[];
}

export interface EventSyncStatus {
  eventId?: number;
  crStatus?: number;
  athenaStatus?: number;
  status?: string;
  isCancelInProgress?: boolean;
}
export enum EventType {
  clientEvent = 'clientEvent',
  adminEvent = 'adminEvent',
  unAvailable = 'na',
  phantomClientEvent = 'phantomClientEvent',
  phantomAdminEvent = 'phantomAdminEvent',
  availability = 'availability',
  observation = 'observation',
  phantomObserverEvent = 'phantomObserverEvent',
  evalSlot = 'evalSlot'
}

export enum Status {
  scheduledRecurring = 'Scheduled - Recurring',
  scheduledOneTime = 'Scheduled - One Time',
  canceled = 'canceled',
  rescheduled = 'rescheduled',
  pending = 'pending',
  scheduled = 'scheduled',
  invisible = 'invisible',
  needsReschedule = 'needsReschedule',
  deleted = 'deleted'
}
/**
 * Billable-hours totals and the client roster derived from the slots the provider
 * recurring template actually draws, so those sidebar figures can never disagree
 * with what the scheduler sees. Booked hours are not here — the backend owns them
 * (providerCapacity.providerBookedHours). `providerId` guards against showing one
 * provider's totals on another's page.
 */
export interface IProviderTemplateDayHours {
  day: string;
  availableHours: number;
}

export interface IProviderCaseloadSession {
  /** 'Mon' … 'Sun', matching the breakdown order. */
  weekday: string;
  /** Slot start, e.g. '9:00 AM'. */
  time: string;
  /** True while the series is confirmed but has not begun yet. */
  isUpcoming: boolean;
  /** 'Aug 27' when isUpcoming, otherwise absent. */
  startsOn?: string;
}

export interface IProviderCaseloadClient {
  clientId: number;
  name: string;
  /** Raw date from the client profile; formatted at render time. */
  dob?: string;
  sessions: IProviderCaseloadSession[];
}

export interface IProviderTemplateSummary {
  providerId?: number;
  /** Bookable billable hours in the template — lunch, meetings and notes excluded. */
  availableHours: number;
  /** Hours of lunch, meetings and notes left out of availableHours. */
  excludedHours: number;
  /** Distinct clients across the standing week. */
  uniqueClients: number;
  /** Per-day figures so a scheduler can check the total by hand. */
  perDay: IProviderTemplateDayHours[];
  /** The clients behind uniqueClients, earliest slot in the week first. */
  caseload: IProviderCaseloadClient[];
}

export interface IProviderAppointmentHours {
  [key: string]: {
    directHours: number;
    indirecthours: number;
    totalHours: number;
    isABA?: boolean;
  };
}
